import i18n from './i18n';

/**
 * The API answers in English. Users don't necessarily read English.
 *
 * Echo has no error codes, only human sentences, so this is the seam where a
 * known sentence becomes a translation key. Anything not listed here comes
 * through untouched — better an English reason than a translated shrug.
 * When you add or reword a message in echo, add it here too.
 */

// Exact-match sentences → i18n key (the English default is the sentence itself
// or an existing key's default, so a missing translation still reads fine)
const EXACT: Record<string, string> = {
  // login
  'Invalid username or password': 'backend.invalidCredentials',
  'Invalid credentials': 'backend.invalidCredentials',
  'Please verify your email before logging in': 'backend.emailNotVerified',
  'You must set your password before logging in': 'backend.passwordSetupRequired',
  'Your account does not have access to this application': 'backend.noPlatformAccess',
  'Login failed': 'login.error.failed',
  'Logout failed': 'backend.logoutFailed',
  'Logout all sessions failed': 'backend.logoutAllFailed',
  'Session not found': 'backend.sessionNotFound',
  'Failed to revoke session': 'backend.revokeSessionFailed',
  'Failed to fetch sessions': 'backend.fetchSessionsFailed',

  // no / expired session
  'Unauthorized': 'toast.sessionExpired',
  'User not authenticated': 'toast.sessionExpired',
  'Authentication failed': 'toast.sessionExpired',
  'No access token provided': 'toast.sessionExpired',
  'Invalid or expired access token': 'toast.sessionExpired',
  'No refresh token provided': 'toast.sessionExpired',
  'Invalid or expired refresh token': 'toast.sessionExpired',
  'Token refresh failed': 'toast.sessionExpired',
  'Failed to verify refreshed token': 'toast.sessionExpired',
  'Device mismatch': 'toast.deviceMismatch',
  'Failed to get user info': 'toast.sessionExpired',
  'Missing CSRF cookie': 'backend.securityCheckFailed',

  // permissions
  'Forbidden': 'backend.forbidden',
  'Insufficient permissions': 'backend.forbidden',
  'Only administrators can manage users': 'backend.forbidden',
  'Only administrators can delete users': 'backend.forbidden',
  'Only super administrators can perform this action': 'backend.forbidden',
  'Access Denied': 'backend.noPlatformAccess',

  // generic titles (only reach the user when a route sends no message)
  'Bad Request': 'backend.badRequest',
  'Not Found': 'backend.notFound',
  'Conflict': 'backend.conflict',
  'Too Many Requests': 'toast.rateLimited',
  'Internal Server Error': 'backend.serverError',
  'Account Locked': 'backend.accountLockedTitle',
  'Email Not Verified': 'backend.emailNotVerified',
  'Password Setup Required': 'backend.passwordSetupRequired',

  // users (admin)
  'User not found': 'backend.userNotFound',
  'Username already exists': 'backend.usernameExists',
  'Email already exists': 'backend.emailExists',
  'Email already registered': 'backend.emailExists',
  'Identifier already in use': 'backend.identifierInUse',
  'Invalid email format': 'backend.invalidEmail',
  'Username is required': 'backend.usernameRequired',
  'Email is required': 'backend.emailRequired',
  'Username or email is required': 'backend.identifierRequired',
  'Username is derived from email when login is by email': 'backend.usernameDerivedFromEmail',
  'Only SuperAdmins can create SuperAdmin users': 'backend.onlySuperAdminsCreateSuperAdmins',
  'Only SuperAdmins can manage SuperAdmin status': 'backend.onlySuperAdminsManageSuperAdmins',
  'Cannot update SuperAdmin users': 'backend.cannotUpdateSuperAdmin',
  'Cannot delete SuperAdmin users': 'backend.cannotDeleteSuperAdmin',
  'Cannot reset password for SuperAdmin users': 'backend.cannotResetSuperAdminPassword',
  'Cannot resend invitation for SuperAdmin users': 'backend.cannotResendSuperAdminInvitation',
  'Cannot assign CUSTOMER role to admin-managed users': 'backend.cannotAssignCustomerRole',
  'Cannot assign CUSTOMER role to admin-created users': 'backend.cannotAssignCustomerRole',
  'You cannot modify your own roles': 'backend.cannotModifyOwnRoles',
  'You cannot modify your own SuperAdmin status': 'backend.cannotModifyOwnSuperAdmin',
  'You cannot delete your own account': 'backend.cannotDeleteOwnAccount',
  'You cannot deactivate your own account': 'backend.cannotDeactivateOwnAccount',
  'User has already set a password. Use password reset instead': 'backend.passwordAlreadySet',
  'Failed to create user': 'users.error.create',
  'Failed to update user': 'backend.updateUserFailed',
  'Failed to delete user': 'users.error.delete',
  'Failed to reset password': 'backend.resetPasswordFailed',
  'Failed to resend invitation': 'users.error.resendInvitation',
  'Failed to fetch users': 'backend.fetchUsersFailed',
  'Failed to fetch user': 'backend.fetchUsersFailed',
  'Failed to fetch audit logs': 'backend.fetchActivityFailed',
  'Failed to fetch filter options': 'backend.fetchActivityFailed',

  // account / passwords
  'Profile not found': 'backend.profileNotFound',
  'Failed to fetch profile': 'backend.fetchProfileFailed',
  'Failed to update profile': 'profile.updateError',
  'Current password is incorrect': 'backend.currentPasswordIncorrect',
  'New password must be different from current password': 'backend.newPasswordSameAsCurrent',
  'User not found or password not set': 'backend.passwordNotSet',
  'Password does not meet requirements': 'backend.passwordComplexity',
  'New password does not meet requirements': 'backend.passwordComplexity',
  'Password does not meet complexity requirements': 'backend.passwordComplexity',

  // invitation / reset / verification / registration
  'Invalid or expired invitation token': 'backend.invalidInvitationToken',
  'Failed to verify invitation token': 'backend.verifyInvitationFailed',
  'Failed to set password': 'invitation.error.failed',
  'Invalid or expired reset token': 'backend.invalidResetToken',
  'Invalid or expired verification token': 'backend.invalidVerificationToken',
  'Email already verified': 'backend.emailAlreadyVerified',
  'Email is already verified. You can log in.': 'backend.emailAlreadyVerified',
  'Registration is not available': 'backend.registrationUnavailable',
  'Email verification is not available': 'backend.verificationUnavailable',
  'Registration failed': 'backend.registrationFailed',
  'Verification failed': 'backend.verificationFailed',
  'Failed to resend verification': 'backend.verificationFailed',

  // counters
  'Counter not found': 'backend.counterNotFound',

  // audit-log error texts (activity history)
  'Account is deactivated': 'backend.accountDeactivated',
  'Email not verified': 'backend.emailNotVerified',
  'Invalid password': 'backend.invalidPassword',
  'Failed to send verification email': 'backend.sendVerificationFailed',

  // The PASSWORD_REGEX_MESSAGE value the deploy files document as the example
  'Password must contain uppercase, lowercase, number, and special character': 'backend.passwordPolicyDefault',
  'Failed to get counters': 'counters.loadError',
  'Failed to create counter': 'counters.error.create',
  'Failed to increment counter': 'counters.error.increment',
  'Failed to decrement counter': 'counters.error.decrement',
  'Failed to reset counter': 'counters.error.reset',
  'Failed to delete counter': 'counters.error.delete',
  'Failed to delete all counters': 'counters.error.deleteAll',
};

// Sentences that carry a number
const PATTERNS: Array<[RegExp, string, (m: RegExpExecArray) => Record<string, unknown>]> = [
  [/^Too many failed login attempts\. Try again in (\d+) seconds$/, 'backend.accountLocked', (m) => ({ seconds: Number(m[1]) })],
  [/^Too many login attempts\. Try again in (\d+) seconds$/, 'backend.tooManyLoginAttempts', (m) => ({ seconds: Number(m[1]) })],
  [/^Rate limit exceeded\./, 'toast.rateLimited', () => ({})],
  [/^Password must be at least (\d+) characters$/, 'backend.passwordMinLength', (m) => ({ count: Number(m[1]) })],
  [/^Account locked for (\d+) seconds$/, 'backend.accountLockedFor', (m) => ({ seconds: Number(m[1]) })],
  [/^Invalid password - account locked for (\d+) seconds$/, 'backend.invalidPasswordLocked', (m) => ({ seconds: Number(m[1]) })],
  [/^Platform access denied \((\w+)\)$/, 'backend.platformAccessDenied', (m) => ({ platform: m[1] })],
  // Infra preconditions the API reports as plain sentences ("Redis is required for …")
  [/^Redis is required/, 'backend.serverError', () => ({})],
];

export function translateBackendMessage(message: string): string {
  const key = EXACT[message];
  if (key) return i18n.t(key, message);

  for (const [pattern, patternKey, vars] of PATTERNS) {
    const match = pattern.exec(message);
    if (match) return i18n.t(patternKey, message, vars(match));
  }

  return message;
}
